import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { heroFadeStyle } from './utils/heroFade';
import { useT } from './i18n';
import artwork from './assets/images/artwork.webp';
import artworkD4 from './assets/images/artworkD4.webp';
import loadinggif from './assets/utility/loading.gif';

/* Values that are product names stay literal; only the labels translate. */
const HIGHLIGHTS = [
  { key: 'hero.jackpotsIn', value: 'BTC · ETH · POL · CRANNI' },
  { key: 'hero.poweredBy', value: 'Polygon + Chainlink VRF' },
  { key: 'hero.executorRevenue', valueKey: 'hero.paidInUsdt' },
];

const Hero = ({ setShowDraw }) => {
  const t = useT();
  const [activeImage, setCurrentActiveImage] = useState(loadinggif);
  const [, setLoading] = useState(true);
  const [bgPosition, setBgPosition] = useState('center');

  useEffect(() => {
    const updateActiveImage = () => {
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const aspectRatio = screenHeight ? screenWidth / screenHeight : 0;
      setBgPosition(aspectRatio >= 2.3 ? '65% center' : aspectRatio >= 2 ? '75% center' : 'center');

      const source = screenWidth >= 768 ? artworkD4 : artwork;
      const img = new Image();
      img.src = source;
      img.onload = () => {
        setCurrentActiveImage(source);
        setLoading(false);
      };
    };

    updateActiveImage();
    window.addEventListener('resize', updateActiveImage);
    return () => window.removeEventListener('resize', updateActiveImage);
  }, []);

  const backgroundImageStyle = {
    backgroundColor: '#000000',
    backgroundSize: 'cover',
    backgroundPosition: bgPosition,
    backgroundImage: `url(${activeImage})`,
    transition: 'background-image 0.3s linear',
    ...heroFadeStyle,
  };

  return (
    <section className="relative flex w-full justify-center overflow-hidden">
      {/* Artwork lives on its own layer so the bottom fade dissolves the image and
          its scrims into the page background without touching the copy. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={backgroundImageStyle}
      >
        {/* Keeps the artwork visible on the right and the copy legible on the left.
            On phones the copy spans the full width, so the veil is heavier there. */}
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/55 md:via-black/75 md:to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-black/50" />
      </div>

      <div className="relative mx-auto flex w-full max-w-screen-2xl flex-col justify-center px-4 pb-14 pt-20 sm:px-8 md:min-h-[640px] lg:min-h-[720px] lg:px-10 min-[1920px]:justify-start min-[1920px]:pt-24">
        <div className="max-w-2xl lb-animate-in">
          <span className="lb-chip lb-chip-cyan">
            <span className="lb-dot" aria-hidden="true" />
            {t('hero.badge')}
          </span>

          <h2 className="lb-heading mt-6 text-[2.75rem] leading-[0.95] sm:text-6xl lg:text-7xl">
            {t('hero.titleTop')}
            <span className="mt-1 block lb-gradient-text">
              {t('hero.titleBottom')}
            </span>
          </h2>

          <p className="mt-6 max-w-xl font-medium text-base leading-relaxed text-white/80 sm:text-lg">
            {t('hero.body')}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <button onClick={setShowDraw} className="lb-btn lb-btn-primary lb-btn-lg w-full sm:w-auto">
              {t('common.playNow')}
            </button>
            <Link to="/nftnodes" className="lb-btn lb-btn-ghost lb-btn-lg w-full sm:w-auto">
              {t('hero.becomeExecutor')}
            </Link>
          </div>

          <dl className="mt-10 grid max-w-xl grid-cols-1 gap-3 sm:grid-cols-3">
            {HIGHLIGHTS.map((item) => (
              <div
                key={item.key}
                className="rounded-2xl border border-white/10 bg-black/50 px-4 py-3 backdrop-blur"
              >
                <dt className="lb-label">{t(item.key)}</dt>
                <dd className="mt-1 text-sm font-semibold text-white">
                  {item.valueKey ? t(item.valueKey) : item.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
};

export default Hero;
